import { lazy, Suspense, useState, useEffect } from "react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import MobileCtaBar from "@/components/site/MobileCtaBar";
import HeroPrimary from "@/components/site/landing/HeroPrimary";
import PitchHook from "@/components/site/landing/PitchHook";
import { useSeo } from "@/lib/seo";
import { scrollToSection } from "@/lib/scrollToSection";

// Sections sous la ligne de flottaison : chargées en différé pour réduire le
// bundle initial, le Total Blocking Time et les long tasks au premier rendu.
const Comparison = lazy(() => import("@/components/site/landing/Comparison"));
const PriceCalculator = lazy(() => import("@/components/site/landing/PriceCalculator"));
const FeaturesSection = lazy(() => import("@/components/site/landing/FeaturesSection"));
const Testimonials = lazy(() => import("@/components/site/landing/Testimonials"));
const Pricing = lazy(() => import("@/components/site/landing/Pricing"));
const DevisSection = lazy(() => import("@/components/site/landing/DevisSection"));
const Faq = lazy(() => import("@/components/site/landing/Faq"));
const FinalCta = lazy(() => import("@/components/site/landing/FinalCta"));
const GuidesSection = lazy(() => import("@/components/site/landing/GuidesSection"));
const AiAssistant = lazy(() => import("@/components/site/AiAssistant"));

export default function Landing() {
    useSeo({
        title: "TyCopro · Gérez votre copropriété bénévolement, économisez votre argent, votre temps et votre stress",
        description:
            "Gérez votre copropriété bénévolement et économisez votre argent, votre temps et votre stress : assemblée générale guidée, comptabilité, convocations et courriers légaux AR24, dans une seule application simple et conforme. 0 € pour vous, porté par le budget de la copropriété.",
        canonical: typeof window !== "undefined" ? window.location.origin + "/" : undefined,
    });

    // Le widget de chat (et son bundle framer-motion) est monté en différé : il
    // n'apparaît de toute façon qu'après un délai, donc on évite qu'il pèse sur le
    // premier rendu (TBT mobile).
    const [showAssistant, setShowAssistant] = useState(false);
    useEffect(() => {
        if (typeof window === "undefined") return;
        const reveal = () => setShowAssistant(true);
        if (typeof window.requestIdleCallback === "function") {
            const id = window.requestIdleCallback(reveal, { timeout: 4000 });
            return () => window.cancelIdleCallback?.(id);
        }
        const id = setTimeout(reveal, 3000);
        return () => clearTimeout(id);
    }, []);

    // Arrivée sur la landing avec une ancre (ex. /#tarifs depuis la navbar d'une
    // autre page, le footer ou un article) : on défile vers la section, même si
    // elle est chargée en lazy (l'utilitaire réessaie le temps qu'elle se monte).
    useEffect(() => {
        if (typeof window === "undefined") return;
        const id = window.location.hash.replace("#", "");
        if (id) scrollToSection(id);
    }, []);

    return (
        <div className="min-h-screen bg-white">
            {/*
              Navbar HORS du conteneur qui rogne les débordements horizontaux.
              `overflow-x: hidden` fait de son élément un conteneur de défilement :
              une `position: sticky` à l'intérieur se cale alors sur ce conteneur et
              non sur la fenêtre. Safari refuse purement et simplement de peindre
              l'en-tête dans ce cas (aggravé par le `backdrop-filter` de la barre),
              d'où une barre invisible sur macOS. On isole donc le rognage aux
              sections décoratives, que la navbar ne traverse pas.
            */}
            <Navbar />
            <main className="overflow-x-clip">
                <HeroPrimary />
                <PitchHook />
                {/*
                  Le parcours de valeur (produit, preuves, prix) est placé AVANT le
                  formulaire : on ne demande l'email qu'une fois la confiance établie et
                  le prix affiché en toute transparence. Le comparatif « coût réel » clôt
                  désormais la page (après la FAQ), en dernière preuve avant l'action.
                */}
                <Suspense fallback={null}>
                    <FinalCta />
                    <PriceCalculator />
                    <FeaturesSection />
                    <Testimonials />
                    <Pricing />
                </Suspense>
                {/*
                  Les CTA « Démarrer » pointent TOUTES vers l'inscription de l'app
                  (APP_REGISTER_URL) : plus aucune ne renvoie vers les tarifs. La
                  section Devis (#devis) reste le seul formulaire de la page, pour
                  les visiteurs qui préfèrent être rappelés.
                */}
                <Suspense fallback={null}>
                    <DevisSection />
                    <Faq />
                    {/* Guides du centre d'aide : accès direct aux articles depuis l'accueil. */}
                    <GuidesSection />
                    <Comparison />
                </Suspense>
            </main>
            <Footer />
            <Suspense fallback={null}>
                {showAssistant && <AiAssistant />}
            </Suspense>
            <MobileCtaBar />
        </div>
    );
}
